import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Divider,
} from "@mui/material";

const AboutUsPage = () => {
  return (
    <Box sx={{ bgcolor: "#f9fafb", color: "#1a1a1a", minHeight: "100vh", py: 8 }}>
      <Container maxWidth="md">
        {/* Title */}
        <Typography
          variant="h2"
          fontWeight={700}
          align="center"
          gutterBottom
          sx={{ mb: 4, fontSize: { xs: "2rem", md: "3rem" } }}
        >
          Our Story: A Family Affair
        </Typography>

        {/* Introduction */}
        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
          If you’ve heard whispers about our property rental service, you’re in for a treat. This isn’t just a business—it’s our family’s passion. We’re here to share why we do what we do, and if you’ve got a few minutes, we’ve got a story to tell.
        </Typography>

        {/* Main Narrative */}
        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
          We’re a family-run operation, through and through. No corporate suits, no distant investors—just us, a tight-knit crew who genuinely care about your property and peace of mind. Managing a rental property can be a rollercoaster, and we get it. That’s why we treat every home like it’s ours, pouring heart and soul into making it a success. Our clients aren’t just names on a spreadsheet; they’re partners we root for, and we’re obsessed with making their lives easier.
        </Typography>

        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
          Communication is our superpower. Ever sent an email and gotten a robotic “no-reply” response? Not with us. You’ll always hear back from a real person—usually the same day, often within hours. Call us, email us, heck, send a carrier pigeon if you want (okay, maybe not that last one). Our phone number is on every page of our site, and we don’t hide behind chatbots or endless menus. When you reach out, you’ll get a friendly voice who knows your property and cares about your questions. We’ve never left a client hanging, and we’re not about to start.
        </Typography>

        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
          We’re not here to nickel-and-dime you. Late fees? Not our style. Hidden costs? Nope. Our goal is simple: deliver a service so good you can’t help but tell your friends. We handle the nitty-gritty—tenant screening, bookings, background checks, cleaning coordination—so you can sit back and watch the income roll in. And when we say tenant screening, we mean it. We choose travelers who respect your home, like nurses, doctors, or buisness professionals. No parties, no smoking, no chaos. Your property comes back in better shape than we got it, every time.
        </Typography>

        <Divider sx={{ my: 4 }} />

        {/* Core Values */}
        <Typography
          variant="h4"
          fontWeight={600}
          gutterBottom
          sx={{ mb: 3 }}
        >
          What We Stand For
        </Typography>

        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 2 }}>
          <strong>Family First.</strong> As a family business, we bring warmth and trust to everything we do. We’re in this together, and your success is our success.
        </Typography>

        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 2 }}>
          <strong>Always Listening.</strong> We’re here when you need us. Every email gets a reply, every call gets a response, and every concern gets our full attention.
        </Typography>

        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 2 }}>
          <strong>Stress-Free Service.</strong> We take the hassle out of property rental management, so you can enjoy the rewards without the headaches.
        </Typography>

        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
          <strong>Reliability.</strong> Enjoy seamless, automatic payments—always on time, worry-free.
        </Typography>

        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
          Why do we do it this way? Because we’ve been on the other side—dealing with faceless companies that don’t pick up the phone or care about your needs. That’s not us. We’re building something different: a business where integrity, communication, and client happiness come first. It’s not about being flashy or chasing trends. It’s about doing right by you, every single day.
        </Typography>

        <Divider sx={{ my: 4 }} />

        {/* Call to Action */}
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="h4"
            fontWeight={600}
            gutterBottom
            sx={{ mb: 3 }}
          >
            Ready to Join Our Family?
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 600, mx: "auto" }}
          >
            Let’s make your property rental journey effortless and rewarding. Reach out today to see how we can partner with you.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            href="mailto:contact@globalga.co?subject=Property%20Rental%20Partnership%20Inquiry"
            sx={{
              px: 6,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              bgcolor: "#1976d2",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              transition: "transform 0.3s, box-shadow 0.3s",
              "&:hover": {
                bgcolor: "#1565c0",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            Get in Touch
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default AboutUsPage;